import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, MailCheck } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import BrainLogo from "@/components/BrainLogo";
import api from "@/lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center px-5 mesh-bg">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-[400px]"
      >
        <Link to="/" className="flex items-center justify-center gap-2 mb-12">
          <BrainLogo size={32} />
          <span className="font-semibold tracking-tight text-[18px]">Studivault</span>
        </Link>

        <AnimatePresence mode="wait">
          {!sent ? (
            <motion.div key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <div className="text-center mb-10">
                <h1 className="display-3">Forgot password?</h1>
                <p className="text-[15px] text-[#B0AEBC] mt-2">
                  Enter your email and we'll send you a reset link.
                </p>
              </div>

              <form onSubmit={onSubmit} data-testid="forgot-password-form" className="space-y-3">
                <div>
                  <label className="text-[12px] font-medium text-[#B0AEBC] mb-1.5 block">Email</label>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="forgot-email-input"
                    className="input"
                    placeholder="you@email.com"
                    autoFocus
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="forgot-submit-button"
                  className="btn btn-primary w-full mt-6 py-3.5 text-[15px]"
                >
                  {loading ? "Sending…" : <>Send reset link <ArrowRight size={15} className="inline ml-1 -mt-0.5" /></>}
                </button>
              </form>

              <p className="text-center text-[14px] text-[#B0AEBC] mt-8">
                Remembered it?{" "}
                <Link to="/login" className="text-[#B8A4FF] hover:underline" data-testid="back-to-login-link">
                  Back to sign in
                </Link>
              </p>
            </motion.div>
          ) : (
            <motion.div
              key="sent"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center"
            >
              <div className="w-16 h-16 rounded-2xl bg-[#1a1a2e] border border-[#2a2a3e] flex items-center justify-center mx-auto mb-6">
                <MailCheck size={28} className="text-[#B8A4FF]" />
              </div>
              <h1 className="display-3 mb-3">Check your inbox</h1>
              <p className="text-[15px] text-[#B0AEBC] mb-8">
                If <strong className="text-white">{email}</strong> is registered, you'll receive a reset link shortly. 
                It expires in 1 hour.
              </p>
              <Link
                to="/login"
                data-testid="sent-back-to-login"
                className="btn btn-primary py-3.5 text-[15px] w-full block text-center"
              >
                Back to sign in
              </Link>
              <button
                onClick={() => { setSent(false); setEmail(""); }}
                className="mt-4 text-[13px] text-[#B0AEBC] hover:text-white w-full"
              >
                Try a different email
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}
